import gsap from 'gsap';
import { createScrollTrigger } from 'scripts/lib/gsap/scrollTrigger';

import Section from 'scripts/core/section';
import Video from 'scripts/components/common/video';
import { CampaignsPageSections, HomePageSections } from 'common/views/sectionTypes';
import { HeaderColorClass, setupTriggerChangeColorHeader, ChangeColorHeaderConfig } from 'scripts/modules/changeColor';
import { Easings } from 'scripts/utils/tweenHelpers';
import { inFrames } from 'scripts/utils/inFrames';
import { Breakpoints } from 'common/views/appBreakpoints';

export default class WorksSection extends Section {
    private _video: Video;
    private _videoInterval: ReturnType<typeof setInterval>;

    private get _isCampaignsPage() { return this.element.id === CampaignsPageSections.Works; }

    private get _title() { return this.element.querySelector('.works-title'); }
    private get _content() { return this.element.querySelector('.works-content'); }
    private get _list() { return this.element.querySelector('.works-list'); }
    private get _listItems() { return this.element.querySelectorAll('.works-item'); }
    private get _listItemsWithoutLast() { return this.element.querySelectorAll('.works-item:not(:last-child)'); }
    private get _lastNumber() { return this.element.querySelector('.works-item:last-child .works-item__number'); }
    private get _lastContent() { return this.element.querySelector('.works-item:last-child .works-item__content'); }
    private get _videoWrap() { return this.element.querySelector('.works-video'); }
    private get _lastListItemText() { return this._listItems[this._listItems.length - 1].querySelector('.works-item__text'); }

    private _videoProgress = {
        currentTime: 0,
    };


    async setupSection() {
        this._changeHeaderColor();

        this._horizontalScroll();

        await this._videoAnimation();

        this._listAnimation();

        this._exitAnimation();
    }

    activate() {
        this._setVideoInterval();
    }

    deactivate() {
        this._disposeVideoInterval();
    }

    private _changeHeaderColor = () => {
        const colorConfigGreen: ChangeColorHeaderConfig = {
            element: this.element,
            color: HeaderColorClass.Green,
            triggerVars: {
                start: 'top center',
                end: !this._isCampaignsPage ? 'bottom bottom' : 'bottom top',
            },
        };

        setupTriggerChangeColorHeader(colorConfigGreen);
    };


    private _pinVideoTablet = () => {
        // Pin distance is depended on which page we are.
        // We have different endTrigger and end point in Campaigns Page
        createScrollTrigger({
            trigger: this._videoWrap,
            start: 'top center-=40%',
            endTrigger: this._isCampaignsPage ? this._lastListItemText : this._content,
            end: this._isCampaignsPage ? 'bottom+=10% bottom' : 'bottom top' ,
            pin: true,
            pinSpacing: false,
            pinReparent: true,
            scrub: 1,
            fastScrollEnd: true,
        });
    };


    private _pinVideoDesktop = () => {
        createScrollTrigger({
            trigger: this._videoWrap,
            start: 'top center-=25%',
            endTrigger: this._listItems[this._listItems.length - 1],
            end: 'top center-=25%',
            pin: true,
            pinSpacing: false,
            pinReparent: true,
            scrub: 1,
            fastScrollEnd: true,
        });
    };


    private _playVideoAnimationOnScroll = () => {
        if (Breakpoints.isDesktop) {
            this._pinVideoDesktop();
        } else {
            this._pinVideoTablet();
        }


        // Push current scroll progress to ref and set in to video in interval
        const tlPlayVideo = gsap.timeline();
        tlPlayVideo.fromTo(this._videoProgress, { currentTime: 0 }, { ease: 'none', currentTime: 4.35 });

        createScrollTrigger({
            trigger: this._list,
            start: Breakpoints.isDesktop ? 'top-=24% center' : 'top-=15% center',
            endTrigger: this._lastListItemText,
            end: 'top center',
            scrub: true,
            animation: tlPlayVideo,
        });
    };

    private _setVideoInterval() {
        if (this._videoInterval) {
            return;
        }

        this._videoInterval = setInterval(() => {
            const progress = this._videoProgress.currentTime.toFixed(3);

            if (this._video.video.currentTime != Number(progress)) {
                this._video.video.currentTime = Number(progress);
            }
        }, 33);
    }

    private _disposeVideoInterval() {
        if (!this._videoInterval) {
            return;
        }

        clearInterval(this._videoInterval);
        this._videoInterval = null;
    }

    private _horizontalScroll = () => {
        const widthTitle = this._title.getBoundingClientRect().width;
        const xTransformTitle = widthTitle - window.innerWidth;

        const tlTitle = gsap.timeline()
            .fromTo(this._title, {
                yPercent: -100,
                x: window.innerWidth,
                ease: 'none',
            }, {
                yPercent: -100,
                x: -xTransformTitle,
            });

        createScrollTrigger({
            trigger: '.works-main-wrapper',
            start: 'top bottom',
            end: 'bottom bottom',
            scrub: true,
            pin: '.works-main',
            pinSpacing: false,
            animation: tlTitle,
            onLeave: () => {
                gsap.set(this.element, { backgroundColor: '#171715' });
            },
            onEnterBack: () => {
                gsap.set(this.element, { backgroundColor: 'transparent' });
            },
        });

        createScrollTrigger({
            trigger: this._content,
            start: 'top bottom',
            endTrigger: this._list,
            end: 'top center',
            scrub: 1,
            fastScrollEnd: true,
        });
    };


    private _exitAnimation = () => {
        // Exit animation for campaigns page you can find in pages/CampaignsPage.ts
        // In other cases it will use this exit animation
        if (this._isCampaignsPage) {
            return;
        }

        const tlExit = gsap.timeline({ defaults: { ease: Easings.customEaseInOut } });
        if (Breakpoints.isDesktop) {
            tlExit
                .fromTo(this._video.video, { opacity: 1, scale: 1 }, { opacity: 0, scale: 0.5, duration: inFrames(50) })
                .to(this._lastNumber, { opacity: 0, scale: 0.5, duration: inFrames(50) }, inFrames(5))
                .to(this._lastContent, { opacity: 0, scale: 0.5, duration: inFrames(50) }, inFrames(5));
            createScrollTrigger({
                trigger: this._list,
                start: 'bottom-=7% bottom',
                end: 'bottom top',
                pin: this.element,
                pinSpacing: false,
                scrub: 1,
                animation: tlExit,
            });
        } else {
            tlExit
                .fromTo(this._video.video, { y: 0 }, { y: 100 })
                .fromTo(this._lastNumber, { y: 0 }, { y: 100 }, '<')
                .fromTo(this._lastContent, { y: 0 }, { y: 100 }, '<');
            createScrollTrigger({
                trigger: this._list,
                start: 'bottom bottom',
                end: 'bottom top',
                pin: true,
                pinSpacing: false,
                scrub: 1,
                animation: tlExit,
            });
        }
    };


    private _videoAnimation = async () => {
        this._video = await new Video({ el: this.element.querySelector('.video-js') }).setup();

        const videoElement = this._video.video as HTMLVideoElement;

        document.documentElement.addEventListener('touchstart', () => {
            videoElement.load();
        }, { once: true });

        this._playVideoAnimationOnScroll();
    };


    private _listAnimation = () => {
        this._listItems.forEach((item, index) => {
            createScrollTrigger({
                trigger: item,
                start: 'top bottom',
                end: 'top top',
                scrub: 1,
                fastScrollEnd: true,
                animation: gsap.timeline().fromTo(this._listItems[index], { opacity: 0.2 }, { opacity: 1 }),
            });
        });

        this._listItemsWithoutLast.forEach((item, index) => {
            createScrollTrigger({
                trigger: item,
                start: 'top top',
                end: 'bottom top',
                scrub: 1,
                fastScrollEnd: true,
                animation: gsap.timeline().fromTo(this._listItems[index], { opacity: 1 }, { opacity: 0.2 }),
            });
        });
    };
}


const currentElement = document.getElementById(HomePageSections.Works) || document.getElementById(CampaignsPageSections.Works);
new WorksSection({ el: currentElement }).setup();
